import React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { faTint } from '@fortawesome/free-solid-svg-icons';
import { PanelMarker } from '../../models/PanelMarker';
import { MathUtils } from '../../utils';

type Props = { panelMarker: PanelMarker };
type State = { chartPosition: number | undefined };

const TINT_PATH = faTint.icon[4] as string;
const TINT_VIEWBOX = `0 0 ${faTint.icon[0]} ${faTint.icon[1]}`;

export class MiniReferenceRangeChart extends React.Component<Props, State> {
  // Compute on mount synchronously so the drop indicator is present in the
  // very first render — important for html2pdf, which snapshots the live DOM
  // and would otherwise miss a deferred state update.
  constructor(props: Props) {
    super(props);
    this.state = { chartPosition: this.computePosition(props.panelMarker) };
  }

  componentWillReceiveProps(nextProps: Props) {
    this.setState({ chartPosition: this.computePosition(nextProps.panelMarker) });
  }

  isEGFRType(pm: PanelMarker): boolean {
    return !!(pm.isEGFR?.() || pm.field?.useEGFRChart);
  }

  computePosition(pm: PanelMarker): number | undefined {
    if (!pm?.hasValue?.() || !pm.field) return undefined;
    const f = pm.field;
    let raw: number | undefined;
    if (this.isEGFRType(pm)) {
      raw = pm.eGFRChartValue?.();
    } else if (f.useRDWChart) {
      raw = pm.altChartValue?.();
    } else if (f.noRedChart) {
      raw = pm.altChartValueNoRed?.();
    } else if (f.noRedOrangeChart) {
      raw = pm.altChartValueNoRedOrange?.();
    } else {
      raw = pm.percentFromMean;
    }
    if (raw == null) return undefined;
    const min = this.getMin(pm);
    const max = this.getMax(pm);
    return Math.max(min, Math.min(max, MathUtils.round(raw, pm.field.precision)));
  }

  getMin(pm: PanelMarker): number {
    if (this.isEGFRType(pm)) return 0;
    if (pm.field?.noRedChart || pm.field?.noRedOrangeChart || pm.field?.useRDWChart) return 0;
    return -133.3;
  }

  getMax(pm: PanelMarker): number {
    if (this.isEGFRType(pm)) return 300;
    if (pm.field?.noRedChart || pm.field?.noRedOrangeChart || pm.field?.useRDWChart) return 266.67;
    return 133.3;
  }

  getTickPositions(pm: PanelMarker): number[] {
    if (this.isEGFRType(pm)) {
      return [0, 33.33, 66.67, 100, 133.33, 166.67, 200, 233.33, 266.67, 300];
    }
    if (pm.field?.useRDWChart) {
      return [0, 166.67, 200, 233.33, 266.67];
    }
    if (pm.field?.noRedChart) {
      return [0, 66, 100, 133.33, 166.67, 200, 233.33, 266.67];
    }
    if (pm.field?.noRedOrangeChart) {
      return [0, 100, 133.33, 166.67, 200, 233.33, 266.67];
    }
    return [-133.3, -100, -66.6, -33.3, 0, 33.3, 66.6, 100, 133.3];
  }

  getPlotBands(pm: PanelMarker): Highcharts.AxisPlotBandsOptions[] {
    // Pastel band palette — softened tints of the app's traffic-light colors,
    // used only by these Markers Outside Optimal mini charts so a wall of
    // stacked bars reads calmly. The full-strength palette
    // (#78F880/#FEFA83/#FFD37E/#FC6552) still drives the per-section
    // ReferenceRangeChart and the gauges elsewhere.
    const GREEN = '#D4EBD5';
    const YELLOW = '#F6F0C4';
    const ORANGE = '#F9E0C4';
    const RED = '#F6CEC6';
    if (this.isEGFRType(pm)) {
      return [
        { from: 0, to: 33.33, color: RED },
        { from: 33.33, to: 66.67, color: ORANGE },
        { from: 66.67, to: 100, color: YELLOW },
        { from: 100, to: 300, color: GREEN },
      ];
    }
    if (pm.field?.useRDWChart) {
      return [
        { from: 0, to: 166.67, color: GREEN },
        { from: 166.67, to: 200, color: YELLOW },
        { from: 200, to: 233.33, color: ORANGE },
        { from: 233.33, to: 266.67, color: RED },
      ];
    }
    if (pm.field?.noRedChart) {
      return [
        { from: 0, to: 66, color: ORANGE },
        { from: 66, to: 100, color: YELLOW },
        { from: 100, to: 166.67, color: GREEN },
        { from: 166.67, to: 200, color: YELLOW },
        { from: 200, to: 233.33, color: ORANGE },
        { from: 233.33, to: 266.67, color: RED },
      ];
    }
    if (pm.field?.noRedOrangeChart) {
      return [
        { from: 0, to: 100, color: YELLOW },
        { from: 100, to: 166.67, color: GREEN },
        { from: 166.67, to: 200, color: YELLOW },
        { from: 200, to: 233.33, color: ORANGE },
        { from: 233.33, to: 266.67, color: RED },
      ];
    }
    return [
      { from: -133.3, to: -100, color: RED },
      { from: -100, to: -66.6, color: ORANGE },
      { from: -66.6, to: -33.3, color: YELLOW },
      { from: -33.3, to: 33.3, color: GREEN },
      { from: 33.3, to: 66.6, color: YELLOW },
      { from: 66.6, to: 100, color: ORANGE },
      { from: 100, to: 133.3, color: RED },
    ];
  }

  getFormatter(pm: PanelMarker): (this: any) => string {
    const digits = pm.field?.precision ?? 1;
    const r = pm.range;
    const f = pm.field;
    return function(this: any) {
      const v: number = this.value;
      if (!r) return '';
      if (pm.isEGFR?.() || f?.useEGFRChart) {
        if (v === 33.33)  return `${MathUtils.round(r.alarmMin, digits)}`;
        if (v === 66.67)  return `${MathUtils.round(r.labMin, digits)}`;
        if (v === 100)    return `${MathUtils.round(r.optimalMin, digits)}`;
        if (v === 133.33) return `${MathUtils.round(r.optimalMax, digits)}`;
        if (v === 200)    return `${MathUtils.round(r.labMax, digits)}`;
        if (v === 233.33) return `${MathUtils.round(r.alarmMax, digits)}`;
        return '';
      }
      if (f?.useRDWChart) {
        if (v === 0)      return `${MathUtils.round(r.optimalMin, digits)}`;
        if (v === 166.67) return `${MathUtils.round(r.optimalMax, digits)}`;
        if (v === 200)    return `${MathUtils.round(r.labMax, digits)}`;
        if (v === 233.33) return `${MathUtils.round(r.alarmMax, digits)}`;
        return '';
      }
      if (f?.noRedChart) {
        if (v === 0)      return `${MathUtils.round(r.alarmMin, digits)}`;
        if (v === 66)     return `${MathUtils.round(r.labMin, digits)}`;
        if (v === 100)    return `${MathUtils.round(r.optimalMin, digits)}`;
        if (v === 166.67) return `${MathUtils.round(r.optimalMax, digits)}`;
        if (v === 200)    return `${MathUtils.round(r.labMax, digits)}`;
        if (v === 233.33) return `${MathUtils.round(r.alarmMax, digits)}`;
        return '';
      }
      if (f?.noRedOrangeChart) {
        if (v === 0)      return `${MathUtils.round(r.labMin, digits)}`;
        if (v === 100)    return `${MathUtils.round(r.optimalMin, digits)}`;
        if (v === 166.67) return `${MathUtils.round(r.optimalMax, digits)}`;
        if (v === 200)    return `${MathUtils.round(r.labMax, digits)}`;
        if (v === 233.33) return `${MathUtils.round(r.alarmMax, digits)}`;
        return '';
      }
      // standard
      if (v === -133.3) return '';
      if (v === -100)   return `${MathUtils.round(r.alarmMin, digits)}`;
      if (v === -66.6)  return `${MathUtils.round(r.labMin, digits)}`;
      if (v === -33.3)  return `${MathUtils.round(r.optimalMin, digits)}`;
      if (v === 0)      return `${MathUtils.round(r.mean, digits)}`;
      if (v === 33.3)   return `${MathUtils.round(r.optimalMax, digits)}`;
      if (v === 66.6)   return `${MathUtils.round(r.labMax, digits)}`;
      if (v === 100)    return `${MathUtils.round(r.alarmMax, digits)}`;
      if (v === 133.3)  return '';
      return '';
    };
  }

  render() {
    const { panelMarker: pm } = this.props;
    const { chartPosition } = this.state;
    if (!pm.hasValue?.() || !pm.range) return null;

    const min = this.getMin(pm);

    const options: Highcharts.Options = {
      chart: {
        type: 'bar',
        height: 36,
        marginTop: 1,
        marginBottom: 22,
        marginLeft: 14,
        marginRight: 14,
        animation: false,
        backgroundColor: 'transparent',
      },
      title: { text: '' },
      xAxis: [{ labels: { enabled: false }, tickLength: 0, lineWidth: 0 }],
      yAxis: {
        title: { text: '' },
        labels: {
          enabled: true,
          formatter: this.getFormatter(pm),
          style: { fontSize: '9px', color: '#555' },
        },
        min: this.getMin(pm),
        max: this.getMax(pm),
        tickPositions: this.getTickPositions(pm),
        gridLineWidth: 0,
        lineWidth: 0,
        plotBands: this.getPlotBands(pm),
      },
      series: [{
        type: 'bar',
        data: [chartPosition ?? min],
        color: 'rgba(0,0,0,0)',
        borderWidth: 0,
        enableMouseTracking: false,
        pointWidth: 1,
        dataLabels: { enabled: false },
      } as Highcharts.SeriesBarOptions],
      tooltip: { enabled: false },
      legend: { enabled: false },
      credits: { enabled: false },
      exporting: { enabled: false },
    };

    const max = this.getMax(pm);
    const positionFraction = chartPosition != null
      ? (chartPosition - min) / (max - min)
      : null;

    return (
      <div style={{ width: '100%', marginTop: 2, position: 'relative' }}>
        <HighchartsReact options={options} highcharts={Highcharts} />
        {positionFraction != null && (
          <div
            style={{
              position: 'absolute',
              left: `calc(14px + (100% - 28px) * ${positionFraction} - 6.5px)`,
              top: 1,
              width: 13,
              height: 13,
              pointerEvents: 'none',
              lineHeight: 0,
            }}
          >
            <svg viewBox={TINT_VIEWBOX} width={13} height={13} xmlns="http://www.w3.org/2000/svg">
              <path d={TINT_PATH} fill="#111" />
            </svg>
          </div>
        )}
      </div>
    );
  }
}
